import "./App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import HomePage from "./pages/HomePage";
import TeamPage from "./pages/TeamPage";
import AdminPage from "./pages/AdminPage";
import UniversityPage from "./pages/UniversityPage";
import SchoolsPage from "./pages/SchoolsPage";
import UniversitiesCatalogPage from "./pages/UniversitiesCatalogPage";
import WebinarPage from "./pages/WebinarPage";
import PricingPage from "./pages/PricingPage";
import StudentSupportPage from "./pages/StudentSupportPage";
import CareerTrackPage from "./pages/CareerTrackPage";
import ConsultationPage from "./pages/ConsultationPage";
import TestPage from "./pages/TestPage";
import EnglishTestPage from "./pages/EnglishTestPage";
import StrategyFormPage from "./pages/StrategyFormPage";
import BlogListPage from "./pages/BlogListPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import WebinarStudygriPage from "./pages/WebinarStudygriPage";
import IntroScreen from "./components/ui/IntroScreen";
import AudioPlayer from "./components/ui/AudioPlayer";
import { Toaster } from "./components/ui/toaster";
import { LangProvider } from "./i18n/LangContext";
import { REDIRECTS } from "./data/redirects";

const INTRO_FLAG = "sg_intro_seen";

// Inner component so we can use useLocation()
const RoutedApp = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [showIntro, setShowIntro] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return isHome && sessionStorage.getItem(INTRO_FLAG) !== "1";
    } catch {
      return isHome;
    }
  });

  useEffect(() => {
    if (showIntro) {
      try { document.body.style.overflow = "hidden"; } catch {}
      return () => {
        try { document.body.style.overflow = ""; } catch {}
      };
    }
  }, [showIntro]);

  const handleIntroDone = () => {
    try { sessionStorage.setItem(INTRO_FLAG, "1"); } catch {}
    setShowIntro(false);
  };

  return (
    <>
      {showIntro && <IntroScreen onDone={handleIntroDone} />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/university/:slug" element={<UniversityPage />} />
        <Route path="/schools" element={<SchoolsPage />} />
        <Route path="/universities" element={<UniversitiesCatalogPage />} />
        <Route path="/webinar" element={<WebinarPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/student-support" element={<StudentSupportPage />} />
        <Route path="/career-track" element={<CareerTrackPage />} />
        <Route path="/consultation" element={<ConsultationPage />} />
        <Route path="/test" element={<TestPage />} />
        <Route path="/english-test" element={<EnglishTestPage />} />
        <Route path="/strategy-form" element={<StrategyFormPage />} />
        {/* Blog: language prefix is mandatory. Without prefix → client-side 301 handled before BrowserRouter mounts. */}
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        <Route path="/webinarstudygri" element={<WebinarStudygriPage />} />
        {/* Legacy 301 redirects (client-side) */}
        {REDIRECTS.map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}
        {/* Catch-all: any other unknown URL → home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

// Language URL prefix — supported codes (ua == uk alias)
const VALID_LANGS = ["uk", "ua", "en", "ru", "pl", "it", "es", "ar", "zh"];

const detectBasename = () => {
  if (typeof window === "undefined") return "";
  const seg = window.location.pathname.split("/").filter(Boolean)[0];
  return seg && VALID_LANGS.includes(seg.toLowerCase()) ? `/${seg.toLowerCase()}` : "";
};

function App() {
  const basename = detectBasename();
  // Blog requires a language prefix ALWAYS. If user hits /blog or /blog/... without
  // a locale prefix, do a client-side 301-style redirect to /ua/blog(/...) before
  // React Router mounts. This prevents duplicate content between /blog and /ua/blog.
  if (typeof window !== "undefined" && !basename) {
    const p = window.location.pathname;
    if (p === "/blog" || p.startsWith("/blog/")) {
      window.location.replace(`/ua${p}${window.location.search}${window.location.hash}`);
      return null;
    }
  }
  return (
    <div className="App">
      <HelmetProvider>
        <LangProvider basename={basename}>
          <BrowserRouter basename={basename}>
            <RoutedApp />
          </BrowserRouter>
          <AudioPlayer />
          <Toaster />
        </LangProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
